// Mirrors backend/services/qg_calc.py exactly using BigInt fixed-point (4dp inputs, 2dp money, half-up).
const FIELD_LABELS = {
  description: "Description", product_name: "Product / equipment name", quantity: "Quantity",
  unit: "Unit", rate: "Rate", area_sqft: "Area (sq.ft)", lump_sum: "Lump-sum amount",
};

export const PROJECT_FIELDS = [
  ["quotation_number", "Quotation number"],
  ["client_name", "Client / customer name"],
  ["project_name", "Project name"],
  ["location", "Project location / address"],
  ["quotation_date", "Quotation date"],
  ["version_label", "Version / revision"],
  ["prepared_by", "Proposal prepared by"],
];

const divRound = (n, d) => (n * 2n + d) / (2n * d);

// "" -> null ; invalid -> {error} ; else BigInt scaled by 10^4 (half-up)
export function parseNum(v) {
  if (v === null || v === undefined) return { val: null };
  const s = String(v).trim().replace(/,/g, "");
  if (s === "") return { val: null };
  if (!/^\+?(\d+\.?\d*|\.\d+)$/.test(s)) {
    return /^-/.test(s) ? { error: "must be zero or positive" } : { error: "not a number" };
  }
  const [intPart, fracRaw = ""] = s.replace("+", "").split(".");
  const frac = (fracRaw + "00000").slice(0, 5);
  const scaled5 = BigInt((intPart || "0") + frac);
  return { val: divRound(scaled5, 10n) };
}

const toCents4 = (v4) => divRound(v4, 100n);

export function formatINR(cents, symbol = false) {
  if (cents === null || cents === undefined) return "";
  const c = typeof cents === "bigint" ? cents : BigInt(Math.round(Number(cents) * 100));
  const neg = c < 0n;
  const abs = neg ? -c : c;
  let whole = (abs / 100n).toString();
  const frac = (abs % 100n).toString().padStart(2, "0");
  if (whole.length > 3) {
    let head = whole.slice(0, -3);
    const tail = whole.slice(-3);
    const parts = [];
    while (head.length > 2) { parts.unshift(head.slice(-2)); head = head.slice(0, -2); }
    if (head) parts.unshift(head);
    whole = `${parts.join(",")},${tail}`;
  }
  return `${symbol ? "₹ " : ""}${neg ? "-" : ""}${whole}.${frac}`;
}

export const centsFromStr = (s) => (s === null || s === undefined ? null : BigInt(String(s).replace(".", "")));

export function itemLabel(item) {
  let text = ((item.product_name || item.description || "").trim().split("\n")[0]) || "";
  if (text.length > 60) text = `${text.slice(0, 60)}…`;
  return `${(item.sr_no || "").trim()} ${text}`.trim() || "Untitled item";
}

export function itemState(item, group) {
  if (item.excluded) return "excluded";
  if (group.has_package) {
    if (group.pricing_choice === "package" && item.package_role === "component") return "covered";
    if (group.pricing_choice === "itemized" && item.package_role === "package") return "covered";
  }
  return "billable";
}

const NEED = {
  qty_rate: ["quantity", "rate"], area_total: ["area_sqft", "rate"],
  qty_area: ["quantity", "area_sqft", "rate"], lump_sum: ["lump_sum"],
};

export function computeItem(item, section) {
  const fields = new Set(section.columns.map((c) => c.field));
  const missing = [];
  if (!(item.description || "").trim() && !(item.product_name || "").trim()) missing.push(["description", "Description is required"]);
  if (fields.has("unit") && !(item.unit || "").trim()) missing.push(["unit", "Unit is required"]);
  const basis = item.pricing_basis || "qty_rate";
  const vals = {};
  ["quantity", "rate", "area_sqft", "lump_sum"].forEach((f) => {
    const p = parseNum(item[f]);
    if (p.error) { vals[f] = null; missing.push([f, `${FIELD_LABELS[f]} ${p.error}`]); }
    else vals[f] = p.val;
  });
  const need = NEED[basis] || NEED.qty_rate;
  const bad = new Set(missing.map(([f]) => f));
  need.forEach((f) => {
    if (vals[f] === null && !bad.has(f)) missing.push([f, `${FIELD_LABELS[f]} is required`]);
    else if ((f === "quantity" || f === "area_sqft") && vals[f] !== null && vals[f] === 0n) missing.push([f, `${FIELD_LABELS[f]} must be greater than zero`]);
  });
  const rc = vals.rate === null ? null : toCents4(vals.rate);
  if (need.includes("rate") && rc !== null && vals.rate !== null && toCents4(vals.rate) === 0n && !item.zero_rate_confirmed) {
    missing.push(["rate", "Rate is 0 — tick 'Included at no extra charge' or enter a rate"]);
  }
  if ((basis === "area_total" || basis === "qty_area") && item.area_needs_review && !item.area_confirmed) {
    missing.push(["area_sqft", "Template area needs review — confirm the area"]);
  }
  if (missing.some(([f]) => need.includes(f) || f === "description" || f === "unit")) return { amount: null, missing };
  let amount;
  if (basis === "qty_rate") amount = divRound(vals.quantity * rc, 10000n);
  else if (basis === "area_total") amount = divRound(vals.area_sqft * rc, 10000n);
  else if (basis === "qty_area") amount = divRound(vals.quantity * vals.area_sqft * rc, 100000000n);
  else amount = toCents4(vals.lump_sum);
  return { amount, missing };
}

export function computeQuotation(q) {
  const errors = [];
  PROJECT_FIELDS.forEach(([f, label]) => {
    if (!String(q[f] ?? "").trim()) errors.push({ section_key: "details", section_name: "Project details", field: f, message: `${label} is required` });
  });
  const categories = {};
  let total = 0n;
  (q.sections || []).forEach((sec) => {
    const { key, name } = sec;
    let secErrors = [];
    let catTotal = 0n;
    let subtotals = {};
    const items = {};
    let included = 0;
    sec.groups.forEach((grp) => {
      if (grp.has_package && !grp.pricing_choice && !sec.not_applicable) {
        secErrors.push({ section_key: key, section_name: name, group_id: grp.id, field: "pricing_choice", message: `${grp.title || "Group"}: choose package price or itemized pricing` });
      }
      grp.items.forEach((it) => {
        const state = itemState(it, grp);
        let amount = null;
        let missing = [];
        if (state === "billable") {
          included += 1;
          ({ amount, missing } = computeItem(it, sec));
          if (amount !== null) {
            catTotal += amount;
            if (grp.subtotal_key) subtotals[grp.subtotal_key] = (subtotals[grp.subtotal_key] || 0n) + amount;
          }
          missing.forEach(([field, message]) => secErrors.push({ section_key: key, section_name: name, group_id: grp.id, item_id: it.id, item_label: itemLabel(it), field, message }));
        }
        items[it.id] = { state, amount, complete: state !== "billable" || missing.length === 0, missing: missing.map(([f]) => f) };
      });
    });
    let status;
    if (sec.not_applicable) {
      status = "not_applicable"; secErrors = []; catTotal = 0n; subtotals = {};
    } else {
      if (included === 0) secErrors.push({ section_key: key, section_name: name, field: "items", message: "No items included — add items or mark this section Not applicable" });
      if (!sec.reviewed) secErrors.push({ section_key: key, section_name: name, field: "reviewed", message: "Section not yet marked as reviewed/complete" });
      if (sec.reviewed && secErrors.length === 0) status = "complete";
      else if (sec.reviewed || sec.touched) status = "in_progress";
      else status = "not_started";
    }
    errors.push(...secErrors);
    total += catTotal;
    categories[key] = { total: catTotal, subtotals, status, errors: secErrors, items };
  });
  const keys = q.interior_rate_sections || [];
  const interiorTotal = keys.reduce((s, k) => s + (categories[k] ? categories[k].total : 0n), 0n);
  const area = parseNum(q.interior_area_sqft);
  const perSqft = area.val && area.val > 0n ? divRound(interiorTotal * 10000n, area.val) : null;
  return { categories, total, errors, canFinalize: errors.length === 0, interior: { total: interiorTotal, perSqft, areaValid: !!(area.val && area.val > 0n) } };
}
